import React from 'react';
import { dpsTheme } from '../../../styles/dpsTheme';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface ActionStats {
  completed: number;
  inProgress: number;
  planned: number;
  total: number;
}

interface DashboardStatsProps {
  initialScore: number;
  currentScore: number;
  progression: number;
  actionStats: ActionStats;
  budgetSpent: number;
  budgetRemaining: number;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({
  initialScore,
  currentScore,
  progression,
  actionStats,
  budgetSpent,
  budgetRemaining,
}) => {
  const { isMobile } = useDeviceSize();
  const completionPercentage = Math.round(
    ((actionStats.completed + actionStats.inProgress) / actionStats.total) * 100
  );

  const roi = budgetSpent > 0 ? ((currentScore - initialScore) / (budgetSpent / 1000)).toFixed(2) : '0.00';

  return (
    <div
      style={{
        background: dpsTheme.colors.background.card,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${dpsTheme.colors.border.primary}`,
        borderRadius: dpsTheme.borderRadius.lg,
        padding: isMobile ? dpsTheme.spacing.xl : dpsTheme.spacing['2xl'],
      }}
    >
      <h2
        style={{
          fontSize: dpsTheme.typography.fontSize['2xl'],
          fontWeight: dpsTheme.typography.fontWeight.semibold,
          color: dpsTheme.colors.text.primary,
          marginBottom: dpsTheme.spacing.xl,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
        Tableau de bord
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: dpsTheme.spacing.lg,
          marginBottom: dpsTheme.spacing['2xl'],
        }}
      >
        <div
          style={{
            background: dpsTheme.colors.background.dark,
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderLeft: `3px solid ${dpsTheme.colors.text.disabled}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: dpsTheme.spacing.xl,
          }}
        >
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}
          >
            Score initial
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize['5xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
            }}
          >
            {initialScore}
          </div>
        </div>

        <div
          style={{
            background: dpsTheme.colors.background.dark,
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderLeft: `3px solid ${dpsTheme.colors.accent.blue.primary}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: dpsTheme.spacing.xl,
          }}
        >
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}
          >
            Score actuel
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize['5xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
            }}
          >
            {currentScore}
          </div>
        </div>

        <div
          style={{
            background: dpsTheme.colors.background.dark,
            border: `1px solid ${dpsTheme.colors.border.secondary}`,
            borderLeft: `3px solid ${dpsTheme.colors.accent.green.primary}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: dpsTheme.spacing.xl,
          }}
        >
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}
          >
            Progression
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize['5xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.accent.green.primary,
            }}
          >
            +{progression}
          </div>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span
            style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              fontWeight: dpsTheme.typography.fontWeight.medium,
              color: dpsTheme.colors.text.tertiary,
            }}
          >
            Avancement des actions
          </span>
          <span
            style={{
              fontSize: dpsTheme.typography.fontSize.base,
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.accent.green.primary,
            }}
          >
            {completionPercentage}%
          </span>
        </div>

        <div
          style={{
            width: '100%',
            height: '10px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: dpsTheme.spacing.xl,
          }}
        >
          <div
            style={{
              height: '100%',
              background: dpsTheme.colors.accent.green.gradient,
              borderRadius: '5px',
              width: `${completionPercentage}%`,
              transition: 'width 0.8s ease-in-out',
            }}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: dpsTheme.spacing.md,
            marginBottom: dpsTheme.spacing.xl,
          }}
        >
          <div
            style={{
              background: dpsTheme.colors.background.dark,
              border: `1px solid ${dpsTheme.colors.border.secondary}`,
              borderRadius: '10px',
              padding: dpsTheme.spacing.lg,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: dpsTheme.typography.fontSize['4xl'],
                fontWeight: dpsTheme.typography.fontWeight.bold,
                color: dpsTheme.colors.accent.green.primary,
                marginBottom: '4px',
              }}
            >
              {actionStats.completed}
            </div>
            <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
              Réalisées
            </div>
          </div>

          <div
            style={{
              background: dpsTheme.colors.background.dark,
              border: `1px solid ${dpsTheme.colors.border.secondary}`,
              borderRadius: '10px',
              padding: dpsTheme.spacing.lg,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: dpsTheme.typography.fontSize['4xl'],
                fontWeight: dpsTheme.typography.fontWeight.bold,
                color: dpsTheme.colors.priority.medium.text,
                marginBottom: '4px',
              }}
            >
              {actionStats.inProgress}
            </div>
            <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
              En cours
            </div>
          </div>

          <div
            style={{
              background: dpsTheme.colors.background.dark,
              border: `1px solid ${dpsTheme.colors.border.secondary}`,
              borderRadius: '10px',
              padding: dpsTheme.spacing.lg,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: dpsTheme.typography.fontSize['4xl'],
                fontWeight: dpsTheme.typography.fontWeight.bold,
                color: dpsTheme.colors.accent.blue.primary,
                marginBottom: '4px',
              }}
            >
              {actionStats.planned}
            </div>
            <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
              Planifiées
            </div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${dpsTheme.colors.border.primary}`, paddingTop: dpsTheme.spacing.xl }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: dpsTheme.spacing.lg }}>
          <span style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
            Investissement réalisé
          </span>
          <span
            style={{
              fontSize: dpsTheme.typography.fontSize.xl,
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
            }}
          >
            {budgetSpent.toLocaleString('fr-FR')} €
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: dpsTheme.spacing.lg }}>
          <span style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
            Budget estimé restant
          </span>
          <span
            style={{
              fontSize: dpsTheme.typography.fontSize.xl,
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
            }}
          >
            {budgetRemaining.toLocaleString('fr-FR')} €
          </span>
        </div>

        <div
          style={{
            background: dpsTheme.colors.scoreLevel.excellent.bg,
            border: `1px solid ${dpsTheme.colors.scoreLevel.excellent.border}`,
            padding: dpsTheme.spacing.lg,
            borderRadius: '10px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              color: dpsTheme.colors.scoreLevel.excellent.text,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}
          >
            ROI Sécuritaire
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize['3xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.accent.green.primary,
            }}
          >
            {roi} pts/k€
          </div>
        </div>
      </div>
    </div>
  );
};
